'use client';

import {
  type CSSProperties,
  type ReactElement,
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { css, cx } from '@/styled-system/css';

import type { TagPropsInterface } from '../tag';
import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { ProgressStepperCurrentLabel } from './progress-stepper-current-label';
import { progressStepperRootRecipe } from './styles/progress-stepper-root.styles';
import type { ProgressStepperRootPropsInterface } from './types/progress-stepper-root.types';

/**
 * ProgressStepper Root component
 *
 * @since 10.0.0
 */
export const ProgressStepperRoot = forwardRef<HTMLDivElement, ProgressStepperRootPropsInterface>(
  (props, forwardedRef) => {
    const {
      activeStepIndex = 0,
      alignLabels = 'center',
      'aria-label': ariaLabel = 'progress',
      children,
      className,
      'css': cssProp,
      labelAppearance = { base: 'current', md: 'all' },
      mapStepsToProps,
      onStepClick,
      orientation = 'horizontal',
      steps = [],
      style,
      ...rest
    } = props;

    const [activeLabel, setActiveLabel] = useState<{
      label: string;
      meta: string | ReactElement<TagPropsInterface> | null;
    }>({ label: '', meta: null });

    // totalSteps is calculated by `ProgressStepper.Steps`
    const [totalSteps, setTotalSteps] = useState<number>(0);

    const setCurrentLabel = useCallback(
      (label: string, meta: string | ReactElement<TagPropsInterface> | null) => {
        setActiveLabel({ label, meta });
      },
      [],
    );

    const styles = css(progressStepperRootRecipe.raw({ orientation }), cssProp);

    const count = useMemo(() => {
      return totalSteps || steps.length;
    }, [totalSteps, steps]);

    const inlineStyles = useMemo(() => {
      return {
        '--c11n-steps': `${count}`,
        '--c11n-step-width': `${(1 / count) * 100}%`,
        ...style,
      } as CSSProperties;
    }, [style, count]);

    return (
      <ProgressStepperRootContext.Provider
        value={{
          activeStepIndex,
          alignLabels,
          steps,
          labelAppearance,
          mapStepsToProps,
          onStepClick,
          orientation,
          setCurrentLabel,
          setTotalSteps,
        }}
      >
        <section
          aria-label={ariaLabel}
          className={cx('c11n', styles, className)}
          data-c11n-component="ProgressStepper.Root"
          data-c11n-version={__C11N_VERSION__}
          data-orientation={orientation}
          ref={forwardedRef}
          style={inlineStyles}
          {...rest}
        >
          {children}
          <ProgressStepperCurrentLabel label={activeLabel.label} meta={activeLabel.meta} />
        </section>
      </ProgressStepperRootContext.Provider>
    );
  },
);

ProgressStepperRoot.displayName = 'ProgressStepper.Root';
